import {
  checkoutCopy,
  detailCopy,
  summaryCopy,
  type CopyLine,
  type OrderCashbackView,
} from "src/utils/cashback";
import {
  SHOP_TIME_ZONE,
  lastDayInstant,
  shortDayMonth,
} from "src/utils/market-time";

/**
 * The decisions behind <CashbackOrderNote />, kept free of React so they can
 * be tested: which sentences a surface shows, and which surface polls.
 */
export type CashbackNoteVariant = "checkout" | "summary" | "detail";

/**
 * "4. 10." in sl, "4/10" in it: a short day + month in the shop locale, read
 * in the shop market's time zone (`timeZone`, default Ljubljana) — never the
 * viewer's, so a visitor abroad reads the same day the shop means.
 *
 * `endsSentence`: the copy puts the date last and closes the sentence itself
 * ("Akcija velja do {{end}}."). sl, hr, sk, cs and de-AT close a short date
 * with a period of their own, so the banner read "do 6. 10.." — the date's
 * period is dropped and the sentence's stands for both.
 *
 * `lastDay`: the value is an END instant, and the label is the last day on
 * which it still applies — the date of (end − 1 ms). An end at 7. 10. 00:00
 * reads "6. 10.": nothing placed on the 7th earns.
 */
export function shortDateLabel(
  iso: string | null | undefined,
  locale: string,
  opts: { endsSentence?: boolean; timeZone?: string; lastDay?: boolean } = {},
): string {
  if (!iso) return "";
  const when = opts.lastDay ? lastDayInstant(iso) : new Date(iso);
  return shortDayMonth(when, locale, opts.timeZone ?? SHOP_TIME_ZONE, {
    endsSentence: opts.endsSentence,
  });
}

/**
 * Only the thank-you page polls: it opens seconds after payment, while the
 * backend still answers `pending` (pre-flight M4). Checkout and order detail
 * read once.
 */
export const cashbackNotePolls = (variant: CashbackNoteVariant): boolean =>
  variant === "summary";

export function cashbackNoteLines(
  variant: CashbackNoteVariant,
  view: OrderCashbackView,
  fmt: (cents: number) => string,
  ctx: { accountEmail: string | null; locale: string; timeZone?: string },
): CopyLine[] {
  if (variant === "checkout")
    return checkoutCopy(view, fmt, { accountEmail: ctx.accountEmail });
  if (variant === "summary") {
    return summaryCopy(view, fmt, {
      accountEmail: ctx.accountEmail,
      // "Velja do {{date}}." ends on the date.
      expiresLabel: shortDateLabel(view.expiresAt, ctx.locale, {
        endsSentence: true,
        timeZone: ctx.timeZone,
      }),
    });
  }
  const line = detailCopy(view, fmt, {
    date: shortDateLabel(view.grantedAt, ctx.locale, { timeZone: ctx.timeZone }),
    expires: shortDateLabel(view.expiresAt, ctx.locale, { timeZone: ctx.timeZone }),
  });
  return line ? [line] : [];
}
